<template>
  <div class="static-table">
    <fade-transition origin="center top">
      <div
        class="context-menu"
        v-show="!!contextMenuActiveRowKey"
        v-click-outside="closeRowContextMenu"
        :style="{
          left: contextMenuLeft,
          top: contextMenuTop
        }"
      >
        <ul>
          <li>
            <a
              href="#"
              aria-role="button"
              @click.prevent="contextMenuCopyRow"
            >Copy Row</a>
          </li>
        </ul>
      </div>
    </fade-transition>
    <modal class="modal-approve" :show="showLoadConfirmModal" @close="showLoadConfirmModal = false">
      <template slot="header">
        <h3>Load Data</h3>
      </template>
      <template>
        <p>You have unsaved changes. This action will reload data and erase your changes. Do you still want to proceed?</p>
      </template>
      <template slot="footer">
        <button class="btn btn-secondary" @click="showLoadConfirmModal = false">Cancel</button>
        <button class="btn btn-primary" @click="confirmReload">Proceed</button>
      </template>
    </modal>
    <slide-y-up-transition origin="center top">
      <base-alert
        v-show="alert"
        :type="alertType"
        :dismissible="false"
      >
        <span>{{ alert }}</span>
        <button 
          type="button" 
          class="close" 
          aria-label="Close"
          @click="clearAlert"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </base-alert>
    </slide-y-up-transition>
    <div class="d-md-flex">
      <div class="flex-grow-1">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'Static Tables' }">Static Tables</router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">{{ title }}</li>
          </ol>
        </nav>
      </div>
      <div class="filter-toggle-wrapper ml-md-1 mb-3 mb-md-0">
        <button
          class="btn btn-secondary w-100"
          aria-controls="static-table-filters"
          aria-haspopup="true"
          :aria-expanded="showFilters"
          @click="showFilters = !showFilters"
        >Filters</button>
      </div>
      <div class="column-toggle-wrapper ml-md-1">
        <base-dropdown tag="div">
          <template v-slot:title>
            <button class="dropdown-toggle btn btn-secondary m-0 mb-3 mb-md-0 w-100"
               :class="{'no-caret': hideArrow}"
               data-toggle="dropdown">
                <i :class="icon"></i>
                <span class="no-icon">Columns</span>
            </button>
          </template>
          <li v-for="column in columns" :key="column.name">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="`${column.slug}-visible`"
                :checked="!column.hidden"
                @change="column.hidden = !column.hidden"
              />
              <label 
                class="custom-control-label"
                :for="`${column.slug}-visible`"
              >{{ column.title }}</label>
            </div>
          </li>
        </base-dropdown>
      </div>
    </div>
    <slide-y-up-transition origin="center top">
      <div class="static-table-filters-wrapper" v-show="showFilters">
        <button 
          type="button" 
          class="close" 
          aria-label="Close"
          @click="showFilters = false"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="form-group">
          <label for="static-table-search">Search</label>
          <input
            class="form-control"
            type="search"
            placeholder="Search..."
            v-model="search"
            @input="debouncedLoad"
          />
        </div>
        <div class="form-group">
          <label for="static-table-add-filter-field-0">Filters</label>
          <div class="mb-1">
            <button
              class="btn btn-secondary"
              @click="addFilter"
            >
              <span>Add Filter</span>
              <i class="fas fa-plus"/>
            </button>
            <button
              class="btn btn-secondary"
              @click="clearFilters"
              :disabled="!hasFilters"
            >
              <span>Clear Filters</span>
              <i class="fas fa-times"/>
            </button>
          </div>
          <div
            class="form-group form-inline"
            v-for="(filter, filterIndex) in filters"
            :key="filterIndex"
          >
            <label 
              :for="`static-table-filter-field-${filterIndex}`"
              class="sr-only"
            >Filter Column</label>
            <select
              class="form-control mr-1"
              :id="`static-table-filter-field-${filterIndex}`"
              :value="filter.field"
              @input="onFilterFieldInput(filter, $event.target.value)"
            >
              <option 
                v-if="filterIndex === 0"
                value=""
              >Select Column...</option>
              <option
                v-for="column in columns"
                :key="column.name"
                :value="column.slug"
              >{{ column.title }}</option>
            </select>
            <label 
              class="sr-only" 
              :for="`static-table-filter-condition-${filterIndex}`"
            >Condition</label>
            <select
              class="form-control mx-1"
              :id="`static-table-filter-condition-${filterIndex}`"
              v-model="filter.condition"
              @input="debouncedLoad"
            >
              <option value="">=</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
              <option value="gte">&geq;</option>
              <option value="lte">&leq;</option>
              <option value="not">Not</option>
              <option value="contains">Contains</option>
              <option value="lacks">Lacks</option>
            </select>
            <label
              :for="`static-table-filter-value-${filterIndex}`"
              class="sr-only"
            >Value</label>
            <select
              v-if="filter.column && filter.column.type === 'select'"
              :id="`static-table-filter-value-${filterIndex}`"
              class="form-control mx-1 filter-value"
              v-model="filter.value"
              @input="debouncedLoad"
            >
              <option 
                v-for="(optionLabel, optionValue) in filter.column.options" 
                :key="optionValue"
              >{{ optionLabel }}</option>
            </select>
            <input
              v-else
              :id="`static-table-filter-value-${filterIndex}`"
              class="form-control mx-1 filter-value"
              v-model="filter.value"
              @input="debouncedLoad"
            />
            <button
              class="btn btn-secondary"
              @click="removeFilter(filterIndex)"
              :disabled="filterIndex === 0 && !hasFilters"
            >
              <span>Remove Filter</span>
              <i class="fas fa-times"/>
            </button>
          </div>
        </div>
      </div>
    </slide-y-up-transition>
    <div class="row change-buttons">
      <div class="col col-12 col-md-6 mb-3">
        <button
          v-if="user.role == 'administrator'"
          type="button"
          :class="{
            'btn': true,
            'btn-primary': true,
            'd-block': true,
            'd-md-inline-block': true,
            'disabled': !tableIsDirty
          }"
          :disabled="!tableIsDirty"
          @click="saveChanges"
        >Save Changes</button>
        <button
          v-if="user.role == 'administrator'"
          type="button"
          :class="{
            'btn': true,
            'btn-secondary': true,
            'd-block': true,
            'd-md-inline-block': true,
            'disabled': !tableIsDirty
          }"
          :disabled="!tableIsDirty"
          @click="cancelChanges"
        >Cancel Changes</button>
      </div>
      <div class="col col-12 col-md-6 mb-3 text-md-right">
        <file-download-link
          :alerts="false"
          extraClasses="d-md-inline-block mr-md-1"
          :progressBar="false"
          :src="spreadsheetDownloadURL"
          v-on:download-started="setAlert('Preparing file...', 'info', false)"
          v-on:download-completed="setAlert('Download complete.', 'success')"
          v-on:download-failed="setAlert('Download failed!', 'danger')"
        />
        <button
          v-if="user.role == 'administrator'"
          type="button"
          :class="{
            'btn': true,
            'btn-secondary': true,
            'd-block': true,
            'd-md-inline-block': true,
            'disabled': isLoading
          }"
          :disabled="isLoading"
          @click="addRow"
        >
          <span>Add Row</span>
          <i class="fa fa-plus"/>
        </button>
      </div>
    </div>
    <div class="static-table-main-wrapper">
      <div class="table-outer">
        <div class="table-responsive position-relative">
          <base-table 
            :class="{
              'table': true,
              'align-items-bottom': true, 
              'table-flush': true,
              'projects-table': true,
              'loading': isLoading,
              'dirty': tableIsDirty
            }"
            thead-classes="thead-light"
            tbody-classes="list"
            :data="rows"
          >
            <template slot="columns">
              <th><!-- Controls --></th>
              <th v-for="column in columns" 
                :key="column.name"
                :hidden="column.hidden"
                :style="column.headerStyles"
              >
                <span>{{ column.title}}</span>
                <button
                  type="button"
                  class="btn btn-link"
                  @click="toggleSort(column)"
                >
                  <span class="sr-only">Sort</span>
                  <i :class="['fa', getSortIcon(column)]"/>
                </button>
              </th>
            </template>
            <template slot-scope="{row}">
              <td 
                @contextmenu="openRowContextMenu(row[primaryKey], $event)"
                :class="{
                  'dirty-row': rowIsDirty(row[primaryKey]),
                  'delete-queued': recordQueuedForDeletion(row[primaryKey])
                }"
              >
                <button
                  v-if="!recordQueuedForDeletion(row[primaryKey])"
                  v-show="user.role == 'administrator'"
                  type="button"
                  class="btn btn-secondary btn-delete"
                  :title="row[primaryKey] < 0 ? `Delete new record.` : `Delete record ${ row[primaryKey] }.`"
                  @click="queueRecordForDeletion(row[primaryKey])"
                >
                  <i class="fas fa-times"/>
                </button>
                <button
                  v-else
                  v-show="user.role == 'administrator'"
                  type="button"
                  class="btn btn-secondary btn-restore"
                  :title="`Restore record ${ row[primaryKey] }.`"
                  @click="cancelRecordDeletion(row[primaryKey])"
                >
                  <i class="fas fa-undo"/>
                </button>
                <button
                 v-if="user.role == 'administrator'"
                  type="button"
                  class="btn btn-secondary btn-duplicate"
                  :title="row[primaryKey] < 0 ? `Duplicate new record.` : `Duplicate record ${ row[primaryKey] }.`"
                  @click="duplicateRecord(row[primaryKey])"
                >
                  <i class="fas fa-copy"/>
                </button>
              </td>
              <td v-for="column in columns" 
                :key="row[primaryKey] + '-' + column.name"
                :hidden="column.hidden"
                @contextmenu="openRowContextMenu(row[primaryKey], $event)"
                :class="{
                  'dirty': cellIsDirty(row[primaryKey], column.name),
                  'dirty-row': rowIsDirty(row[primaryKey]),
                  'delete-queued': recordQueuedForDeletion(row[primaryKey]),
                  'error': !cellIsValid(row[primaryKey], column.name)
                }"
              >

                <input v-if="column.attributes.readonly === 'readonly'"
                  :type="text"
                  :required="column.attributes.required"
                  :readonly="column.attributes.readonly"
                  :disabled="column.attributes.disabled"
                  :size="column.attributes.size"
                  :style="column.inputStyles"
                  :value="column.name === primaryKey && row[column.name] < 0 ? '' : row[column.name]"
                  @click="e => copyReadOnly(column, e.target.value)"
                />

                <date-picker v-else-if="column.attributes.type === 'datetime-local'"
                  value-type="format"
                  format="YYYY-MM-DD hh:mm:ss"
                  :ref="inputRef(row[primaryKey], column.name)"
                  :value="row[column.name]"
                  @change="e => updateRowCell(row[primaryKey], column.name, e.target.value, e.target._value)"
                  data-toggle="tooltip"
                  data-placement="top"
                  :title="cellValidationMessage(row[primaryKey], column.name)"
                />

                <input v-else-if="column.type === 'input'"
                  :type="column.attributes.type"
                  :required="column.attributes.required"
                  :readonly="column.attributes.readonly"
                  :disabled="column.attributes.disabled"
                  :min="column.attributes.min"
                  :max="column.attributes.max"
                  :maxlength="column.attributes.maxlength"
                  :size="column.attributes.size"
                  :step="column.attributes.step"
                  :style="column.inputStyles"
                  :ref="inputRef(row[primaryKey], column.name)"
                  :value="row[column.name]"
                  @input="e => updateRowCell(row[primaryKey], column.name, e.target.value, e.target._value)"
                  data-toggle="tooltip"
                  data-placement="top"
                  :title="cellValidationMessage(row[primaryKey], column.name)"
                />
                <select v-else-if="column.type === 'select'"
                  :required="column.attributes.required"
                  :readonly="column.attributes.readonly"
                  :disabled="column.attributes.disabled || user.role == 'administrator'? false:true"
                  :style="column.inputStyles"
                  :ref="inputRef(row[primaryKey], column.name)"
                  v-model="row[column.name]"
                  @input="e => updateRowCell(row[primaryKey], column.name, e.target.value, e.target._value)"
                  data-toggle="tooltip"
                  data-placement="top"
                  :title="cellValidationMessage(row[primaryKey], column.name)"
                >
                  <option v-for="(optionLabel, optionValue) in column.options" 
                    :key="optionValue"
                  >{{ optionLabel }}</option>
                </select>
              </td>
            </template>
          </base-table>
        </div>
        <div v-if="isLoading" class="loading-message">
          <p>...Loading...</p>
        </div>
        <div v-else-if="!rows || rows.length === 0" class="d-flex justify-content-center align-items-center">
          <p>No data found.</p>
        </div>
      </div>
      <nav aria-label="Table paging.">
        <div class="form-inline">
          <a
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': page === 1
            }"
            :href="getPageUrl({ page: 1 })"
            :disabled="page === 1"
            @click.prevent="load({ page: 1 })"
          >First</a>
          <a
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': page === 1
            }"
            :href="getPageUrl({ page: Math.max(page - 1, 1) })"
            :disabled="page === 1"
            @click.prevent="load({ page: Math.max(page - 1, 1)})"
          >Previous</a>
          <label for="static-table-page">Page</label>
          <input
            id="static-table-page"
            class="form-control mx-1"
            type="number"
            min="1"
            :max="lastPage"
            v-model="page"
            @input="debouncedLoad"
          >
          <span>of {{ lastPage }}</span>
          <input
            id="static-table-page-size"
            class="form-control mx-1"
            type="number"
            min="1"
            max="100"
            v-model="pageSize"
            @input="debouncedLoad"
          >
          <label for="static-table-page-size">per page</label>
          <a
            :class="{
              'btn': true,
              'btn-secondary': true,
              'ml-1': true,
              'disabled': page === lastPage
            }"
            :href="getPageUrl({ page: Math.min(page + 1, lastPage) })"
            :disabled="page === lastPage"
            @click.prevent="load({ page: Math.min(page + 1, lastPage)})"
          >Next</a>
          <a
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': page === lastPage
            }"
            :href="getPageUrl({ page: lastPage })"
            :disabled="page === lastPage"
            @click.prevent="load({ page: lastPage })"
          >Last</a>
        </div>
      </nav>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import VueClipboard from 'vue-clipboard2'
  import DatePicker from 'vue2-datepicker'
  import FileDownloadLink from '../../components/FileDownloadLink.vue'
  import vueDebounce, { debounce } from 'vue-debounce'
  import { SlideYUpTransition } from 'vue2-transitions'
  import BaseDropdown from '../../components/BaseDropdown.vue'
  Vue.use(VueClipboard)
  Vue.use(vueDebounce, {
    defaultTime: '300ms'
  })
  export default {
    components: { 
      DatePicker,
      FileDownloadLink,
      SlideYUpTransition,
      BaseDropdown
    },
    name: 'static-table',
    props: {
      table: {
        type: String,
        required: true
      }
    },
    data: function() {
      const tableData = require('../../components/StaticTable/data/tables/' + this.table + '.json')
      const params = window.location.search 
        ? window.location.search
          .substr(1)
          .split('&')
          .reduce((allParams, queryPart) => {
            const [ key, value ] = queryPart.split('=')
            allParams[key] = decodeURIComponent(value)
            return allParams
          }, {})
        : {}
      const sorts = []
      const filters = []
      for(let key in params) {
        if(key.match('^sort-')) {
          const slug = key.substr(5)
          const column = tableData.columns.find(column => column.slug === slug)
          if(column) {
            sorts.push({
              slug,
              dir: params[key]
            })
          }
        } else {
          const conditionMatch = key.match(/[gl]te?|contains$/)
          const condition = conditionMatch ? conditionMatch[0] : ''
          const slug = conditionMatch ? key.match(/^([a-z0-9-]+[a-z0-9]+)?-([lg]te?|contains|lacks|not)$/)[0] : key
          const column = tableData.columns.find(column => column.slug === slug)
          if(column) {
            filters.push({
              column,
              field: slug,
              condition,
              value: params[key]
            })
          }
        }
      }
      if(!filters.length) {
        filters.push({
          field: '',
          condition: '',
          value: ''
        })
      }
      const newRowInputColumn = tableData.columns.find(column => !column.attributes.readonly).name
      return {
        apiUrl: tableData.api,
        loadSource: null,
        name: tableData.name,
        slug: tableData.slug,
        title: tableData.title,
        columns: tableData.columns,
        loadsInProgress: 0,
        showFilters: !!params['search'] || filters.length > 1 || filters[0].field,
        filters: filters,
        rows: null,
        page: params['page'] ? parseInt(params['page']) : 1,
        pageSize: params['page-size'] ? parseInt(params['page-size']) : 25,
        lastPage: 1,
        totalRecords: 0,
        search: params['search'] ?? '',
        sorts: sorts,
        alert: null,
        alertType: null,
        alertTimeout: null,
        changedValues: {},
        showLoadConfirmModal: false,
        reloadConfirmed: false,
        primaryKey: tableData.columns[0].name,
        recordsToDelete: [],
        newRowID: 0,
        newRowInputColumn,
        validationErrors: null,
        contextMenuActiveRowKey: null,
        contextMenuLeft: null,
        contextMenuTop: null,
        user: false
      }
    },
    beforeMount() {
        this.user = JSON.parse(localStorage.getItem('user'))
    },
    methods: {
      addFilter() {
        const usedFields = this.filters.map(filter => filter.field)
        const firstFreeColumn = this.columns.find(column => !usedFields.includes(column.slug))
        this.filters.push({
          field: firstFreeColumn ? firstFreeColumn.slug : this.columns[this.filters.length % this.columns.length].slug,
          condition: '',
          value: ''
        })
      },
      removeFilter(filterIndex) {
        if(this.filters.length === 1) {
          this.filters[0].field = ''
          this.filters[0].condition = ''
          this.filters[0].value = ''
        } else {
          this.filters.splice(filterIndex, 1)
        }
        this.load()
      },
      clearFilters() {
        this.filters.splice(1)
        this.removeFilter(0)
      },
      getApiUrl(method, params, overrides) {
        let url = this.apiUrl
        if(method) {
          url += '/' + method
        }
        if(params) {
          if(overrides) {
            for(let key in overrides) {
              params[key] = overrides[key]
            }
          }
          const encodedParams = []
          for(let name in params) {
            encodedParams.push(name + '=' + encodeURIComponent(params[name]))
          }
          if(encodedParams.length) {
            url += '?' + encodedParams.join('&')
          }
        }
        return url
      },
      getPageUrl(overrides) {
        let url = window.location.pathname
        const params = {
          page: this.page,
          'page-size': this.pageSize,
          search: this.search
        }
        for(let i = 0; i < this.sorts.length; i++) {
          const { slug, dir } = this.sorts[i]
          if(dir) {
            params['sort-' + slug] = dir
          }
        }
        for(let i = 0; i < this.filters.length; i++) {
          const { field, condition, value } = this.filters[i];
          if(field) {
            params[field + (condition ? '-' + condition : '')] = value;
          }
        }
        
        if(overrides) {
          for(let key in overrides) {
            params[key] = overrides[key]
          }
        }
        const defaults = {
          page: 1,
          'page-size': 25,
          search: ''
        }
        let appended = false
        for(let key in params) {
          const value = params[key]
          if(value === defaults[key]) {
            continue;
          }
          if(appended) {
            url += '&'
          } else {
            url += '?'
            appended = true
          }
          url += encodeURIComponent(key) + '=' + encodeURIComponent(value)
        }
        return url
      },
      load(overrides) {
        if(overrides) {
          for(let key in overrides) {
            if(this[key] !== null && this[key] !== overrides[key]) {
              this[key] = overrides[key]
            }
          }
        }
        if(this.tableIsDirty && !this.reloadConfirmed) {
          this.showLoadConfirmModal = true
          return
        }
        const pageUrl = this.getPageUrl()
        history.pushState({}, null, pageUrl)
        const apiParams = {
          page: this.page,
          'page-size': this.pageSize,
          search: this.search
        }
        for(let i = 0; i < this.sorts.length; i++) {
          const { slug, dir } = this.sorts[i]
          if(dir) {
            apiParams['sort-' + slug] = dir
          }
        }
        for(let i = 0; i < this.filters.length; i++) {
          const { field, condition, value } = this.filters[i]
          if(field) {
            apiParams[field + (condition ? '-' + condition : '')] = value;
          }
        }
        const apiUrl = this.getApiUrl(null, apiParams)
        if(this.isLoading) {
          this.loadSource.cancel()
        }
        this.loadSource = this.$http.CancelToken.source()
        this.loadsInProgress++
        this.clearAlert()
        
        return this.$http.get(apiUrl, {
          cancelToken: this.loadSource.token
        })
          .then(response => {
            console.log('Loaded', this.loadSource)
            if(!response) {
              throw 'No response.'
            }
            if(!response.data) {
              throw 'No data in response.'
            }
            if(!response.data.data) {
              throw 'No rows in response data.'
            }
            this.lastPage = parseInt(response.data.last_page)
            this.pageSize = parseInt(response.data.per_page)
            this.totalRecords = parseInt(response.data.total)
            if(this.page > this.lastPage) {
              this.page = this.lastPage
              this.load()
            }
            this.clearDirty()
            this.rows = response.data.data
            this.setColumnStyles()
          })
          .catch(error => {
            if(!this.$http.isCancel(error)) {
              this.setAlert('There was a problem loading the data. Please refresh the page and try again.', 'danger')
              console.error(error)
            }
          })
          .finally(() => {
            this.loadsInProgress--
          })
      },
      debouncedLoad: () => {},
      confirmReload() {
        this.reloadConfirmed = true
        this.load()
      },
      setColumnStyles() {
        const columns = this.columns
        for(let i = 0; i < columns.length; i++) {
          const column = columns[i]
          const headerStyles = {}
          const inputStyles = {}
          const words = column.title.split(' ')
          let longestWordLength = 0
          for(let i = 0; i < words.length; i++) {
            const wordLength = words[i].length
            if(wordLength > longestWordLength) {
              longestWordLength = wordLength
            }
          }
          const rows = this.rows
          let longestValueLength = 0
          if(rows && rows.length) {
            const columnName = column.name
            for(let i = 0; i < rows.length; i++) {
              const value = rows[i][columnName]
              const valueLength = value && value.toString ? value.toString().length : 0
              if(valueLength > longestValueLength) {
                longestValueLength = valueLength
              }
            }
          }
          let minInputWidth = longestValueLength
          let minHeaderWidth = longestWordLength
          minInputWidth *= (17 / 16)
          minInputWidth += 2
          minHeaderWidth += 2
          if(column.type === 'select' || column.attributes.type === 'number') {
            if(minInputWidth < 4) {
              minInputWidth = 4
            }
          } else {
            if(minInputWidth < 2) {
              minInputWidth = 2
            }
          }
          headerStyles.minWidth = minHeaderWidth + 'ch'
          inputStyles.minWidth = minInputWidth + 'ch'
          column.headerStyles = headerStyles
          column.inputStyles = inputStyles
        }
      },
      getSortDir(column) {
        const sort = this.sorts.find(sort => sort.slug === column.slug)
        if(!sort) {
          return null
        }
        return sort.dir
      },
      getSortIcon(column) {
        const sortDir = this.getSortDir(column)
        if(sortDir === 'ASC') {
          return 'fa-sort-up'
        } else if(sortDir === 'DESC') {
          return 'fa-sort-down'
        } else {
          return 'fa-sort'
        }
      },
      toggleSort(column) {
        const sortIndex = this.sorts.findIndex(sort => sort.slug === column.slug)
        
        if(sortIndex === -1) {
          this.sorts.push({
            slug: column.slug,
            dir: 'DESC'
          })
        } else if(this.sorts[sortIndex].dir === 'DESC') {
          this.sorts[sortIndex].dir = 'ASC'
        } else {
          this.sorts.splice(sortIndex, 1)
        }
        this.debouncedLoad()
      },
      copyReadOnly(column, value) {
        this.$copyText(value)
          .then(() => this.setAlert(`Copied ${column.title} to clipboard.`, 'success'))
          .catch(() => this.setAlert(`Could not copy ${column.title}!`, 'warning'))
      },
      setAlert(text, type, timeout) {
        this.alert = text
        this.alertType = type ?? 'info'
        if(this.alertTimeout) {
          clearTimeout(this.alertTimeout)
        }
        timeout = timeout || (type === 'danger' ? false : 10000)
        if(timeout) {
          this.alertTimeout = setTimeout(this.clearAlert, timeout)
        }
      },
      clearAlert() {
        this.alert = null
        this.alertType = null
        if(this.alertTimeout) {
          clearTimeout(this.alertTimeout)
        }
        this.alertTimeout = null
      },
      getRowByKey(rowKey) {
        const primaryKey = this.primaryKey
        const row = this.rows.find(row => row[primaryKey] == rowKey)
        return row
      },
      getCellValue(rowKey, columnName) {
        const row = this.getRowBykey(rowKey)
        return row ? row[columnName] : null
      },
      updateRowCell(rowKey, columnName, newValue, oldValue) {
        const changedValues = this.changedValues
        const row = this.getRowByKey(rowKey)
        if(!changedValues[rowKey]) {
          this.$set(changedValues, rowKey, {})
        }
        
        if(changedValues[rowKey][columnName]) {
          changedValues[rowKey][columnName].new = newValue
        } else {
          if(oldValue === undefined) {
            oldValue = row[columnName]
          }
          
          this.$set(changedValues[rowKey], columnName, {
            old: oldValue,
            new: newValue
          })
        }
        row[columnName] = newValue
      },
      cellIsDirty(rowKey, columnName) {
        if(rowKey < 0) {
          return true
        }
        const changedRow = this.changedValues[rowKey]
        if(!changedRow) {
          return false
        }
        const changedCell = changedRow[columnName]
        if(!changedCell) {
          return false
        }
        if(changedCell.old === undefined) {
          return true
        }
        return changedCell.old !== changedCell.new
      },
      rowIsDirty(rowKey) {
        if(rowKey < 0) {
          return true
        }
        if(this.recordQueuedForDeletion(rowKey)) {
          return true
        }
        
        const changedRow = this.changedValues[rowKey]
        if(!changedRow) {
          return false
        }
        for(let columnName in changedRow) {
          const changedCell = changedRow[columnName]
          if(changedCell.old !== changedCell.new) {
            return true
          }
        }
        return false
      },
      clearDirty() {
        this.$set(this, 'changedValues', {})
        this.$set(this, 'recordsToDelete', [])
        const rows = this.rows
        const primaryKey = this.primaryKey
        let newRowIndex
        while(rows && rows.length && ~(newRowIndex = rows.findIndex(row => row[primaryKey] < 0))) {
          rows.splice(newRowIndex, 1)
        }
        this.reloadConfirmed = false
        this.showLoadConfirmModal = false
      },
      saveChanges() {
        if(!this.validate()) {
          this.setAlert('There is a problem with some of the data you entered. Please review your changes and try again.', 'danger')
          return
        }
        this.clearAlert()
        const changedValues = this.changedValues
        const updates = {}
        for(let rowKey in changedValues) {
          if(rowKey > 0) {
            const update = {}
            const changedRow = changedValues[rowKey]
            let rowChanged = false
            for(let columnName in changedRow) {
              const changedCell = changedRow[columnName]
              if(changedCell.old !== changedCell.new) {
                update[columnName] = changedCell.new
                rowChanged = true
              }
            }
            if(rowChanged) {
              updates[rowKey] = update
            }
          }
        }
        const rows = this.rows
        const primaryKey = this.primaryKey
        const inserts = []
        for(let i = 0; i < rows.length; i++) {
          const row = rows[i]
          if(row[primaryKey] < 0) {
            const insert = {}
            for(let columnName in row) {
              insert[columnName] = row[columnName]
            }
            delete insert[primaryKey]
            inserts.push(insert)
          }
        }
        const apiParams = {
          deletes: this.recordsToDelete.map(id => parseInt(id)),
          updates,
          inserts
        }
        const apiUrl = this.getApiUrl('updates')
        this.isLoading = true
        this.clearAlert()
        return this.$http.post(apiUrl, apiParams)
          .then(response => {
            if(!response) {
              throw 'No response.'
            }
            if(!response.data) {
              throw 'No response data.'
            }
            if(!response.data.success) {
              throw 'Request failed.'
            }
            this.reloadConfirmed = true
            const loadResult = this.load()
            const loadResponse = () => {
              this.setAlert('Changes saved.', 'success')
            }
            if(loadResult) {
              return loadResult.then(loadResponse)
            } else {
              loadResponse()
            }
          })
          .catch(error => {
            this.setAlert('There was a problem saving the data. Please try again.', 'danger')
            console.error(error)
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      cancelChanges() {
        const changedValues = this.changedValues
        
        for(let rowKey in changedValues) {
          const changedRow = changedValues[rowKey]
          const dataRow = this.getRowByKey(rowKey)
          for(let columnName in changedRow) {
            const oldValue = changedRow[columnName].old
            const currentValue = dataRow[columnName]
            
            if(oldValue !== currentValue) {
              dataRow[columnName] = oldValue
            }
          }
        }
        const rows = this.rows
        const primaryKey = this.primaryKey
        let newRowIndex
        while(rows && rows.length && ~(newRowIndex = rows.findIndex(row => row[primaryKey] < 0))) {
          rows.splice(newRowIndex, 1)
        }
        this.clearDirty()
      },
      recordQueuedForDeletion(rowKey) {
        return !!~this.recordsToDelete.findIndex(rowPrimaryKey => rowPrimaryKey == rowKey)
      },
      queueRecordForDeletion(rowKey) {
        if(rowKey < 0) {
          this.cancelRecordDeletion(rowKey)
          const index = this.rows.findIndex(row => row[this.primaryKey] == rowKey)
          if(~index) {
            this.rows.splice(index, 1)
          }
        } else {
          this.recordsToDelete.push(rowKey)
        }
      },
      cancelRecordDeletion(rowKey) {
        const index = this.recordsToDelete.findIndex(rowPrimaryKey => rowPrimaryKey == rowKey)
        if(~index) {
          this.recordsToDelete.splice(index, 1)
        }
      },
      duplicateRecord(rowKey) {
        this.addRow()
        const rows = this.rows
        const columns = this.columns
        const primaryKey = this.primaryKey
        const newRowID = this.newRowID
        const originalRow = rows.find(row => row[primaryKey] == rowKey)
        const duplicateRow = rows.find(row => row[primaryKey] == newRowID)
        for(let i = 0; i < columns.length; i++) {
          const column = columns[i]
          const columnName = column.name
          if(columnName !== primaryKey && column.attributes.readonly !== 'readonly') {
            duplicateRow[columnName] = originalRow[columnName]
          }
        }
      },
      addRow() {
        const row = {}
        const columns = this.columns
        
        for(let i = 0; i < columns.length; i++) {
          const column = columns[i]
          const columnName = column.name
          const newValue = column.default || null
          row[columnName] = newValue
        }
        this.newRowID--
        row[this.primaryKey] = this.newRowID
        this.rows.unshift(row)
        this.$nextTick(() => {
          this.$refs.newInput[0].focus()
        })
      },
      inputRef(rowKey, columnName) {
        if(rowKey === this.newRowID && columnName === this.newRowInputColumn) {
          return 'newInput'
        } else {
          return null
        }
      },
      cellIsValid(rowKey, columnName) {
        return !this.cellValidationMessage(rowKey, columnName)
      },
      cellValidationMessage(rowKey, columnName) {
        if(!this.validationErrors) {
          return null
        }
        if(!this.validationErrors[rowKey]) {
          return null
        }
        if(!this.validationErrors[rowKey][columnName]) {
          return null
        }
        return this.validationErrors[rowKey][columnName]
      },
      clearValidationErrors() {
        this.$set(this, 'validationErrors', null)
      },
      validate() {
        const errors = {}
        const rows = this.rows
        const columns = this.columns
        const primaryKey = this.primaryKey
        let hasErrors = false
        for(let i = 0; i < rows.length; i++) {
          const row = rows[i]
          const rowErrors = {}
          let rowHasErrors = false
          for(let j = 0; j < columns.length; j++) {
            const column = columns[j]
            const columnName = column.name
            let columnError = null
            if(!row[columnName] && column.attributes.required === 'required') {
              columnError = 'This field is required.'
            }
            if(columnError) {
              rowErrors[columnName] = columnError
              rowHasErrors = true
            }
          }
          if(rowHasErrors) {
            errors[row[primaryKey]] = rowErrors
            hasErrors = true
          }
        }
        this.$set(this, 'validationErrors', hasErrors ? errors : null)
        return !hasErrors
      },
      openRowContextMenu(rowKey, e) {
        e.preventDefault()
        this.contextMenuActiveRowKey = rowKey
        this.contextMenuLeft = e.x + 'px'
        this.contextMenuTop = e.y + 'px'
      },
      closeRowContextMenu() {
        this.contextMenuActiveRowKey = null
      },
      contextMenuCopyRow() {
        const contextMenuActiveRowKey = this.contextMenuActiveRowKey
        if(!contextMenuActiveRowKey) {
          return
        }
        const primaryKey = this.primaryKey
        const row = this.rows.find(row => row[primaryKey] == contextMenuActiveRowKey)
        let text = ''
        for(let columnName in row) {
          const value = row[columnName]
          text += (value ? value : '') + "\t"
        }
        text = text.trim()
        this.$copyText(text)
          .then(() => this.setAlert(`Copied row ${contextMenuActiveRowKey} to clipboard.`, 'success'))
          .catch(() => this.setAlert(`Could not copy row ${contextMenuActiveRowKey}!`, 'warning'))
          .finally(() => this.closeRowContextMenu())
      },
      onFilterFieldInput(filter, newField) {
        filter.field = newField
        filter.column = this.columns.find(column => column.slug === newField)
        this.debouncedLoad()
      }
    },
    computed: {
      hasFilters() {
        return this.filters.length > 1
          || this.filters[0].field
      },
      isLoading() {
        return !!this.loadsInProgress
      },
      spreadsheetDownloadURL() {
        return this.getApiUrl('excel')
      },
      tableIsDirty() {
        if(this.recordsToDelete.length) {
          return true;
        }
        const changedValues = this.changedValues
        for(let rowKey in changedValues) {
          const changedRow = changedValues[rowKey]
          for(let columnName in changedRow) {
            const changedCell = changedRow[columnName]
            if(changedCell.old !== changedCell.new) {
              return true
            }
          }
        }
        if(this.rows && this.rows.find(row => row[this.primaryKey] < 0)) {
          return true
        }
        return false
      },
    },
    mounted: function() {
      this.debouncedLoad = debounce(this.load, 300)
      this.setColumnStyles()
      this.load()
    }
  };
</script>
<style lang="scss">
  .status:disabled{
    pointer-events: none;
  }
  .static-table {
    & > .alert {
      position: fixed;
      bottom: 8px;
      right: 8px;
      z-index: 2;
    }
    .change-buttons {
      .btn {
        @media (max-width: 768px) {
          width: 100%;
          margin-bottom: 12px;
        }
      }
    }
    .column-toggle-wrapper {
      .dropdown {
        display: block;
        li {
          padding: 0 4px;
        }
        $customControlPadding: 1.5rem;
        .custom-control {
          padding-left: $customControlPadding;
          label {
            &:before,
            &:after {
              left: -$customControlPadding;
            }
          }
        }
      }
      .dropdown-menu.show {
        max-height: 90vh;
        padding: 4px;
        overflow: auto;
        
        @media(min-width: 768px) {
          max-height: 50vh;
        }
      }
    }
    .filter-value {
      flex-grow: 1;
    }
    .static-table-filters-wrapper {
      .form-inline {
        @media (min-width: 768px) {
          flex-wrap: nowrap;
          button {
            flex-shrink: 0;
          }
          select {
            flex-shrink: 1;
            min-width: 0;
          }
        }
      }
    }
    thead tr {
      th {
        position: relative;
        padding: 8px 12px 2px;
        box-sizing: border-box;
        vertical-align: bottom;
        white-space: normal;
        button {
          display: inline-block;
          position: absolute;
          bottom: 0;
          right: 0;
          padding: 0;
          border: none;
          background: none;
          font-size: 12px;
        }
      }
    }
    tbody {
      tr {
        td {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          &:first-child {
            min-width: 36px;
          }
          input[readonly] {
            cursor: copy;
          }
          & input,
          & select {
            width: 100%;
            margin: 0;
            box-sizing: border-box;
            background-color: transparent;
            border-color: transparent;
            &:focus {
              background-color: inherit;
              border-color: inherit;
            }
          }
          &.error {
            & input,
            & select {
              border: 1px red solid;
            }
          }
          &.dirty-row {
            background-color: #ccffee;
          }
          &.dirty {
            background-color: #44ffcc;
          }
          &.delete-queued {
            input {
              text-decoration: line-through;
            }
          }
          .btn {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            width: 18px;
            height: 18px;
            margin: 0;
            padding: 0;
            border: none;
            background: none;
            box-shadow: none;
            i {
              margin: 0;
              padding: 0;
              transform-origin: center center;
              transition: transform .25s ease-in-out;
            }
            &:focus i,
            &:hover i {
              transform: scale(1.1);
            }
          }
        }
        &:nth-child(2n) {
          td {
            &.dirty-row {
              background-color: #bbeedd;
            }
            &.dirty {
              background-color: #33eebb;
            }
          }
        }
      }
    }
    .table-outer {
      position: relative;
    }
    .loading-message {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, .75);
      z-index: 1;
      p {
        display: inline-block;
        padding: 8px 16px;
        border-radius: 4px;
        background-color: grey;
        color: white;
        font-weight: 700;
        text-align: center;
        text-transform: uppercase;
      }
    }
    .context-menu {
      display: inline-block;
      position: fixed;
      padding: 8px;
      border-radius: 4px;
      background-color: white;
      box-shadow: 2px 2px 4px rgba(0, 0, 0, .5);
      ul {
        li {
          border-bottom: 1px #cccccc solid;
          a {
            display: block;
            padding: 4px 0;
            color: currentColor;
          }
        }
      }
      z-index: 1;
    }
  }
</style>